import { TypographyH2 } from "@/components/ui/typography"
import { cn } from "@/lib/utils"
import {
  userControllerSetMyOptions,
  type GetMyOptionsDtoResVoice,
  type GetMyOptionsDtoResVoiceLang,
} from "@/shared/api/generated"
import styles from "./styles.module.scss"
import {
  DISCOVERED_TRANSLATION_KEYS,
  LANGUAGE_VOICES,
} from "@/shared/constants"
import { useState } from "react"
import { ArrowLeft } from "lucide-react"
import { SelectVoiceItem } from "../select-voice-item"
import { VOICES } from "@/shared/constants/voices"
import { capitalize } from "@/shared/utils"
import { Spinner } from "@/components/ui/spinner"
import { useAutoTranslation, useOptionsStore } from "@/entities"
import { Button } from "@/components/ui/button"

const PickLanguagesSection = ({
  onSelectLanguage,
  defaultLanguage,
}: {
  onSelectLanguage: (language: GetMyOptionsDtoResVoiceLang) => void
  defaultLanguage: GetMyOptionsDtoResVoiceLang
}) => {
  const { t } = useAutoTranslation()
  return (
    <div>
      <TypographyH2>
        {t(
          DISCOVERED_TRANSLATION_KEYS.YOU_CAN_SWITCH_LANGUAGE_FOR_YOUR_AUDIO_SVOICE
        )}
      </TypographyH2>

      <div className={styles.languages}>
        {LANGUAGE_VOICES.map(language => (
          <div
            key={`SELECT_SETTINGS_LANGUAGE_${language.id}`}
            onClick={() => onSelectLanguage(language.id)}
            className={cn(styles.language, {
              [styles.language_active]: defaultLanguage === language.id,
            })}
          >
            <div className={styles.language__flag}>{language.flag({})}</div>
            <div className={styles.language__label}>{language.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

const PickVoiceSection = ({
  onSelectVoice,
  selectedLanguage,
  defaultVoice,
  handleBack,
  onChooseVoice,
}: {
  selectedLanguage: GetMyOptionsDtoResVoiceLang
  onSelectVoice: (voice: GetMyOptionsDtoResVoice) => void
  defaultVoice: GetMyOptionsDtoResVoice
  handleBack: () => void
  onChooseVoice: () => void
}) => {
  const { t } = useAutoTranslation()
  return (
    <div className={styles.voicesParent}>
      <div className={styles.voicesHeader}>
        <div className={styles.voicesHeader__left}>
          <button
            onClick={handleBack}
            type="button"
            className={styles.voicesHeaderButton}
          >
            <ArrowLeft />
          </button>
          <TypographyH2>
            {t(DISCOVERED_TRANSLATION_KEYS.NOW_LET_SPICK_ANEW_VOICE)}
          </TypographyH2>
        </div>

        <Button onClick={onChooseVoice}>
          {t(DISCOVERED_TRANSLATION_KEYS.CHOOSE_THIS_VOICE)}
        </Button>
      </div>

      <div className={styles.voicesGrid}>
        {VOICES[selectedLanguage].map(voice => (
          <SelectVoiceItem
            key={`SELECT_VOICE_${voice.id}`}
            {...voice}
            isActive={defaultVoice === voice.id}
            onSelectVoice={() => onSelectVoice(voice.id)}
          />
        ))}
      </div>
    </div>
  )
}

const CurrentVoiceSection = ({
  voiceName,
  voiceLang,
}: {
  voiceName: string
  voiceLang: GetMyOptionsDtoResVoiceLang
}) => {
  const { t } = useAutoTranslation()
  return (
    <div>
      <TypographyH2>
        {t(DISCOVERED_TRANSLATION_KEYS.CURRENT_VOICE)} {capitalize(voiceLang)} -{" "}
        {voiceName}
      </TypographyH2>
    </div>
  )
}

export const ChangeLangAndVoiceSettings = ({
  voice,
  voiceLang,
  voiceName,
}: {
  voiceLang: GetMyOptionsDtoResVoiceLang
  voice: GetMyOptionsDtoResVoice
  voiceName: string
}) => {
  const [selectedLanguage, setSelectedLanguage] =
    useState<GetMyOptionsDtoResVoiceLang | null>(null)

  const [selectedTempVoice, setSelectedTempVoice] =
    useState<GetMyOptionsDtoResVoice | null>(null)
  const [isLoading, setLoading] = useState(false)
  const setOptions = useOptionsStore(s => s.setData)

  const handleSelectVoice = async (voiceId: GetMyOptionsDtoResVoice) => {
    if (!selectedLanguage) return
    setSelectedTempVoice(voiceId)
  }

  const handleSubmit = async () => {
    if (!selectedLanguage || !selectedTempVoice) return
    setLoading(true)

    try {
      const newOptions = await userControllerSetMyOptions({
        voiceLang: selectedLanguage,
        voiceId: selectedTempVoice,
      })
      setOptions(newOptions)
    } finally {
      setLoading(false)
      setSelectedLanguage(null)
    }
  }

  const handleBack = () => {
    setSelectedTempVoice(null)
    setSelectedLanguage(null)
  }

  if (!selectedLanguage) {
    return (
      <div className={styles.page}>
        <CurrentVoiceSection voiceName={voiceName} voiceLang={voiceLang} />
        <PickLanguagesSection
          onSelectLanguage={setSelectedLanguage}
          defaultLanguage={voiceLang}
        />
      </div>
    )
  }

  return (
    <div className={styles.pageRelative}>
      <PickVoiceSection
        onSelectVoice={handleSelectVoice}
        handleBack={handleBack}
        onChooseVoice={handleSubmit}
        selectedLanguage={selectedLanguage}
        defaultVoice={selectedTempVoice || voice}
      />

      {isLoading && (
        <div className={styles.loadingBlurWrapper}>
          <Spinner />
        </div>
      )}
    </div>
  )
}
